import { ChangeEvent, useContext, useState } from "react";
import { useTranslation } from "react-i18next";
import styled from "styled-components";
import {
  Box,
  Button,
  Devider,
  FilterContext,
  Flex,
  InputSearch,
  PaginationPerPageSelect,
  SearchContext,
  Select,
  Text,
  ModalContext,
  Checkbox,
  MultiFileUploadContext,
  PageViewToggle,
  PageViewTypeEnum,
  MediaDragAndDropContext,
  Toggle,
  toast,
} from "dash-ui";
import {
  MediaFileTypesFilterEnum,
  ProgressTrackingEvent,
  ProgressTrackingTypesEnum,
} from "dash-common";

import { BulkActionsContext } from "common/contexts/bulkActionsContext/bulkActionsContext";
import { BreadcrumbContext } from "common/contexts/breadcrumb-context";
import {
  useMoveMediaInSortOrderMutation,
  useObserveProgressTrackingByTypeSubscription,
  useUpdateBrandedFilesReadyMutation,
} from "gql/generated.graphql";
import { stickyTopBarStyles } from "containers/properties/helpers/stickyTopBarStyles";
import { getBrandingTypeOptions } from "../../helpers/brandingTypeOptions";

const SelectAllContainer = styled(Flex)`
  border-radius: ${({ theme }) => theme.space * 2}px;
  border: 1px solid ${({ theme }) => theme.colors.n40};
`;

const PageWrapper = styled(Box)<{ top?: number }>`
  ${stickyTopBarStyles}
`;

const MediaPageHeader = ({
  propertyId,
  folderId,
  property,
}: {
  propertyId: string;
  folderId?: string;
  property?: any;
}) => {
  const { t } = useTranslation(["pages", "common"]);
  const { selected, setIsAllSelectedSpy, isAllSelected } =
    useContext(BulkActionsContext);
  const { setFilterState } = useContext(FilterContext);
  const { setSearchSlug } = useContext(SearchContext);
  const { toggleModal } = useContext(ModalContext);
  const { addProgressTrackingEvent } = useContext(MultiFileUploadContext);
  const [inputValue, setInputValue] = useState("");
  const { mediaIds, resetState } = useContext(MediaDragAndDropContext);
  const { isSticky } = useContext(BreadcrumbContext);

  const [moveMediaInSortOrder] = useMoveMediaInSortOrderMutation();
  const [updateBrandedFilesReady, { loading: updatingBrandedFiles }] =
    useUpdateBrandedFilesReadyMutation();

  const handleBrandedFilesToggle = (checked: boolean) => {
    if (!property?._id) {
      return;
    }

    updateBrandedFilesReady({
      variables: {
        input: {
          propertyId: property._id,
          brandedFilesReady: checked,
        },
      },
      onCompleted: () => {
        toast.success(
          checked
            ? t("pages:propertyPage.brandedFilesReadyEnabled")
            : t("pages:propertyPage.brandedFilesReadyDisabled")
        );
      },
      onError: (error: any) => {
        console.error("Failed to update branded files ready:", error);
        toast.error(t("common:serverMessages.unknown"));
      },
    });
  };

  useObserveProgressTrackingByTypeSubscription({
    variables: { type: ProgressTrackingTypesEnum.FILE_UPLOAD },
    onData({ data }) {
      const incomingProgressTrackingEvent =
        data?.data?.observeProgressTrackingByType.document;

      if (incomingProgressTrackingEvent) {
        addProgressTrackingEvent(
          incomingProgressTrackingEvent as ProgressTrackingEvent
        );
      }
    },
  });

  const setMediaSortOrder = () => {
    moveMediaInSortOrder({
      variables: {
        sortedMediaIds: mediaIds,
      },
      onCompleted: () => {
        resetState();
      },
    });
  };

  return (
    <PageWrapper w="100%" pos="relative">
      <Flex px={6} py={4} alignItems="center" justifyContent="space-between">
        <Text lg>{t("pages:propertyPage.mediaFiles")}</Text>
        <Flex alignItems="center">
          <Button
            mr={4}
            px={10.5}
            onClick={setMediaSortOrder}
            disabled={!mediaIds.length}
          >
            {t("common:labels.save")}
          </Button>
          <PageViewToggle
            defaultButton={PageViewTypeEnum.LIST}
            buttons={[
              { icon: "apps", viewType: PageViewTypeEnum.CARDS },
              { icon: "list", viewType: PageViewTypeEnum.LIST },
            ]}
          />
        </Flex>
      </Flex>
      <Devider />
      <Box px={6} py={3}>
        <Flex alignItems="center" mb={2}>
          <Box mr={2}>
            <Toggle
              checked={property?.brandedFilesReady || false}
              onChange={(e) => handleBrandedFilesToggle(e.target.checked)}
              disabled={updatingBrandedFiles}
            />
          </Box>
          <Text semibold>{t("common:labels.brandedFilesReady")}</Text>
        </Flex>
        <Box ml={2}>
          <Text sm color="n60">
            {t("forms:messages.brandedFilesReadyHelp")}
          </Text>
        </Box>
      </Box>
      <Devider />
      <Flex py={4} px={6} alignItems="center" flexWrap="wrap">
        <SelectAllContainer minWidth={122} mr={3} mb={3} p={2}>
          <Flex>
            <Checkbox
              checked={isAllSelected}
              mr={2}
              onChange={(checked) => setIsAllSelectedSpy(checked)}
            />
          </Flex>
          <Flex>
            <Text>{t("common:labels:selectAll")}</Text>
          </Flex>
        </SelectAllContainer>
        <Box flex="1" minWidth={200} mr={3} mb={3}>
          <InputSearch
            w="100%"
            value={inputValue}
            placeholder={t("pages:propertyPage.searchMedia") || ""}
            onChange={(e: ChangeEvent<HTMLInputElement>) => {
              setSearchSlug(e.target.value);
              setInputValue(e.target.value);
            }}
            onClear={() => {
              setSearchSlug("");
              setInputValue("");
            }}
          />
        </Box>
        <PaginationPerPageSelect
          w={130}
          mr={3}
          mb={3}
          label={t("common:labels.show") || ""}
          options={[20, 40, 60, 100]}
          defaultValue={40}
          menuPosition={isSticky ? "fixed" : "absolute"}
        />
        <Select
          menuPosition={isSticky ? "fixed" : "absolute"}
          w={144}
          mr={3}
          mb={3}
          isMulti
          onChanged={(_: string, values?: string[]) =>
            setFilterState("fileTypes", values as MediaFileTypesFilterEnum[])
          }
          placeholder={t("pages:propertyPage.allFileTypes")}
          options={Object.values(MediaFileTypesFilterEnum).map((item) => ({
            label: item,
            value: item,
          }))}
        />
        <Select
          menuPosition={isSticky ? "fixed" : "absolute"}
          w={180}
          mr={3}
          mb={3}
          isSearchable={false}
          onChanged={(value: string) =>
            setFilterState("brandingType", value || undefined)
          }
          placeholder={t("common:labels.allBrandingTypes")}
          options={getBrandingTypeOptions(t, true)}
        />
        <Button
          onClick={() => toggleModal(true, "SetFolderNameModal")}
          variation="primary"
          icon="plus"
          mr={3}
          mb={3}
          w={119}
        >
          {t("common:labels.folder")}
        </Button>
        <Button
          w={119}
          mb={3}
          variation="primary"
          icon="plus"
          onClick={() => toggleModal(true, "UploadMediaWithBrandingModal")}
        >
          {t("common:labels.file")}
        </Button>
      </Flex>
      <Devider />
    </PageWrapper>
  );
};

export default MediaPageHeader;
